// 手机输入和消息工具分行呈现，保留清晰触控面积与可滚动详情；桌面布局及业务操作沿用原实现。
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn, ZoomOut, Download, ChevronLeft, ChevronRight, RotateCw, Maximize2 } from "lucide-react";
import { fileUrl } from "../../lib/chat";
import { useOverlayPortal } from "../../lib/overlayPortal";

/* 聊天图片灯箱：window 事件 chat:lightbox {images:[{fileId,filename}], index} 打开；滚轮 / 按钮缩放 · 1x 左右滑动切图 · 放大后拖拽平移 · 双击 1x/2x · 旋转 · 下载 · Esc 关闭 */
export const openLightbox = (images, index = 0) => window.dispatchEvent(new CustomEvent("chat:lightbox", { detail: { images, index } }));

export function ChatLightbox({ zh }) {
  const portal = useOverlayPortal(); // 图片预览与聊天共用顶层，普通聊天页仍挂到 body。
  const [st, setSt] = useState(null);   // {images, index}
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [nat, setNat] = useState(null);
  const drag = useRef(null);
  const suppressClick = useRef(false);
  const T = (a, b) => (zh ? a : b);
  const reset = () => { setZoom(1); setRot(0); setPan({ x: 0, y: 0 }); };
  const go = useCallback((d) => setSt((s) => { if (!s) return s; const n = (s.index + d + s.images.length) % s.images.length; return { ...s, index: n }; }), []);

  useEffect(() => {
    const on = (e) => { const d = e.detail || {}; if (d.images?.length) { setSt({ images: d.images, index: Math.min(Math.max(0, d.index || 0), d.images.length - 1) }); reset(); } };
    window.addEventListener("chat:lightbox", on); return () => window.removeEventListener("chat:lightbox", on);
  }, []);
  useEffect(() => { reset(); setNat(null); }, [st?.index]);
  useEffect(() => {
    if (!st) return undefined;
    const k = (e) => { if (e.key === "Escape") setSt(null); else if (e.key === "ArrowLeft") go(-1); else if (e.key === "ArrowRight") go(1); else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(8, z * 1.25)); else if (e.key === "-") setZoom((z) => Math.max(0.2, z / 1.25)); };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  }, [st, go]);
  if (!st) return null;
  const img = st.images[st.index];
  const url = fileUrl(img.fileId);
  const wheel = (e) => { e.preventDefault(); setZoom((z) => Math.min(8, Math.max(0.2, z * (e.deltaY < 0 ? 1.15 : 1 / 1.15)))); };
  // 需求 FB5fa78cc0c7：触屏用户反馈图片无法左右滑动切换。
  // 只在原尺寸且存在多图时把水平手势识别为切图；放大后继续保留原有的任意方向平移，
  // 避免一次手势既切换图片又改动查看位置。阈值同时要求横向明显大于纵向，防止纵向滚动误触。
  const down = (e) => {
    if (e.button !== 0) return;
    // 左右切换按钮也在图片舞台内。按钮的 pointerdown 若冒泡到舞台并触发指针捕获，
    // 浏览器会把随后的 click 重定向到舞台，误触发“点击空白处关闭灯箱”。
    // 交互控件的手势交给按钮自身处理，这里只接管舞台或图片上的拖拽 / 滑动。
    if (e.target.closest?.("button")) return;
    suppressClick.current = false;
    drag.current = {
      x: e.clientX - pan.x,
      y: e.clientY - pan.y,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
      swiping: false,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    if (!drag.current.swiping && zoom <= 1.01 && st.images.length > 1
      && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      drag.current.swiping = true;
    }
    if (drag.current.swiping) {
      drag.current.moved = true;
      return;
    }
    const nx = e.clientX - drag.current.x;
    const ny = e.clientY - drag.current.y;
    if (Math.abs(nx - pan.x) + Math.abs(ny - pan.y) > 2) drag.current.moved = true;
    setPan({ x: nx, y: ny });
  };
  const up = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const didSwipe = d.swiping && Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.25;
    suppressClick.current = didSwipe || d.moved;
    drag.current = null;
    if (didSwipe) go(dx < 0 ? 1 : -1);
  };
  const cancel = () => {
    suppressClick.current = Boolean(drag.current?.moved);
    drag.current = null;
  };
  const download = async () => {
    try {
      const r = await fetch(url); const b = await r.blob(); const u = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = u; a.download = img.filename || `image_${img.fileId}.png`; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(u), 2000);
    } catch { window.open(url, "_blank", "noopener"); }
  };
  const ib = "h-11 w-11 shrink-0 rounded-full flex items-center justify-center text-white/85 hover:bg-white/15 transition-colors disabled:opacity-30";
  return createPortal(
    <div data-testid="chat-lightbox" data-index={st.index} data-zoom={zoom.toFixed(2)} className="fixed inset-0 z-[10003] bg-slate-950/92 flex flex-col select-none" onClick={(e) => { if (e.target === e.currentTarget) setSt(null); }}>
      <div className="mobile-safe-topbar h-12 shrink-0 flex items-center gap-2 px-3 text-white/85 text-xs" onClick={(e) => e.stopPropagation()}>
        <span className="truncate font-medium" data-testid="chat-lightbox-name">{img.filename || img.fileId}</span>
        {nat && <span className="text-white/50">{nat.w} × {nat.h}</span>}
        <span className="text-white/50" data-testid="chat-lightbox-counter">{st.index + 1} / {st.images.length}</span>
        <span className="flex-1" />
        <button type="button" data-testid="chat-lightbox-zoom-out" title={T("缩小 (-)", "Zoom out")} onClick={() => setZoom((z) => Math.max(0.2, z / 1.25))} className={ib}><ZoomOut className="h-4 w-4" /></button>
        <span className="w-12 text-center tabular-nums" data-testid="chat-lightbox-zoom">{Math.round(zoom * 100)}%</span>
        <button type="button" data-testid="chat-lightbox-zoom-in" title={T("放大 (+)", "Zoom in")} onClick={() => setZoom((z) => Math.min(8, z * 1.25))} className={ib}><ZoomIn className="h-4 w-4" /></button>
        <button type="button" data-testid="chat-lightbox-fit" title={T("适应屏幕", "Fit")} onClick={reset} className={ib}><Maximize2 className="h-4 w-4" /></button>
        <button type="button" data-testid="chat-lightbox-rotate" title={T("旋转", "Rotate")} onClick={() => setRot((r) => (r + 90) % 360)} className={ib}><RotateCw className="h-4 w-4" /></button>
        <button type="button" data-testid="chat-lightbox-download" title={T("下载原图", "Download")} onClick={download} className={ib}><Download className="h-4 w-4" /></button>
        <button type="button" data-testid="chat-lightbox-close" title={T("关闭 (Esc)", "Close")} onClick={() => setSt(null)} className={`${ib} hover:bg-red-600`}><X className="h-4 w-4" /></button>
      </div>
      <div data-testid="chat-lightbox-stage" className="flex-1 min-h-0 relative overflow-hidden flex items-center justify-center touch-none" onWheel={wheel} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={cancel} onClick={(e) => {
        const suppressed = suppressClick.current;
        suppressClick.current = false;
        if (!suppressed && e.target === e.currentTarget) setSt(null);
      }}>
        <img key={img.fileId} src={url} alt={img.filename || ""} draggable={false} data-testid="chat-lightbox-image" onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          onDoubleClick={() => { setZoom((z) => (z > 1.01 ? 1 : 2)); setPan({ x: 0, y: 0 }); }}
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rot}deg)`, transition: drag.current ? "none" : "transform .12s ease-out", maxWidth: "calc(100vw - 32px)", maxHeight: "calc(100vh - 96px)" }}
          className={`object-contain shadow-2xl ${zoom > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`} />
        {st.images.length > 1 && (<>
          <button type="button" data-testid="chat-lightbox-prev" onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60"><ChevronLeft className="h-6 w-6" /></button>
          <button type="button" data-testid="chat-lightbox-next" onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60"><ChevronRight className="h-6 w-6" /></button>
        </>)}
      </div>
    </div>, portal || document.body);
}
